<template>
  <v-container fluid class="pa-4 pa-sm-6">
    <!-- Шапка страницы -->
    <PageTitle title="Управление темами Q&A" />
     <v-alert class=" mb-4" text="Справочник тем. Каждая тема группирует вопросы студентов и помогает ИИ точнее подбирать контекст." type="info" variant="tonal"></v-alert>
    <!-- Таблица и кнопка добавления -->
    <v-card class="pa-4 rounded-lg" outlined elevation="1">
      <v-data-table
        :headers="headers"
        :items="topics"
        :loading="isLoading"
        loading-text="Загрузка списка тем..."
        no-data-text="Список тем пуст"
      >
        <!-- Верхняя панель таблицы с кнопкой создания -->
        <template v-slot:top>
          <v-toolbar flat color="transparent">
            <v-toolbar-title class="text-h6 font-weight-bold px-0 text-deep-orange-darken-1">
              Доступные тематики
            </v-toolbar-title>
            <v-spacer></v-spacer>

            <!-- Модальное окно Создания / Редактирования -->
            <v-dialog v-model="dialog" max-width="500px" persistent>
              <template v-slot:activator="{ props }">
                <v-btn color="deep-orange-darken-1" class="font-weight-bold" prepend-icon="mdi-plus" v-bind="props">
                  Добавить тему
                </v-btn>
              </template>

              <v-card class="rounded-lg ">
                <v-card-title class="text-h6 font-weight-bold px-4 pt-4 bg-deep-orange-darken-1">
                  {{ formTitle }}
                </v-card-title>

                <v-card-text class="px-4">
                  <v-form ref="topicForm" v-model="isFormValid">
                    <v-text-field
                      v-model="editedItem.topicTitle"
                      label="Название темы"
                      variant="outlined"
                      density="comfortable"
                      :rules="[v => !!v || 'Название обязательно', v => v.length >= 3 || 'Минимум 3 символа']"
                      class="mb-2"
                    ></v-text-field>

                    <v-textarea
                      v-model="editedItem.topicDescription"
                      label="Описание темы (для контекста RAG)"
                      variant="outlined"
                      density="comfortable"
                      rows="3"
                      :rules="[v => !!v || 'Описание обязательно']"
                      placeholder="Опишите, какие вопросы входят в эту категорию..."
                    ></v-textarea>
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-4 pb-4">
                  <v-spacer></v-spacer>
                  <v-btn color="grey-darken-1" variant="text" @click="close">Отмена</v-btn>
                  <v-btn color="deep-orange-darken-1" variant="flat" :disabled="!isFormValid" :loading="isSaving" @click="save">
                    Сохранить
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Диалог подтверждения удаления -->
            <v-dialog v-model="dialogDelete" max-width="400px">
              <v-card class="rounded-lg pa-2">
                <v-card-title class="text-h6 font-weight-bold text-center text-deep-orange-darken-1 pt-4">Удаление темы</v-card-title>
                <v-card-text class="text-center text-body-1 py-2">
                  Вы уверены, что хотите удалить тему <br><strong>«{{ editedItem.topicTitle }}»</strong>?
                </v-card-text>
                <v-card-actions class="justify-center pb-4">
                  <v-btn color="grey-darken-1" variant="text" @click="closeDelete">Отмена</v-btn>
                  <v-btn color="error" variant="flat" :loading="isSaving" @click="deleteItemConfirm">Удалить</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

       <!-- Кастомный вывод Описания с ограничением длины -->
        <template v-slot:[`item.topicDescription`]="{ item }">
            <div class="text-body-2 text-truncate-description text-grey-darken-2">
                {{ item.topicDescription }}
            </div>
        </template>
        <!-- Колонка действий (Редактировать / Удалить) -->
        <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end gap-1">
                <v-btn icon="mdi-pencil" size="small" variant="text" color="blue" @click="editItem(item)"></v-btn>
                <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="deleteItem(item)"></v-btn>
            </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>
<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import PageTitle from "@/elements/PageTitle.vue";
import { customAxios } from "@/service/customAxios";
// Состояния загрузок и модальных окон
const isLoading = ref(false)
const isSaving = ref(false)
const dialog = ref(false)
const dialogDelete = ref(false)
const isFormValid = ref(false)
const topicForm = ref(null)

// Список топиков
const topics = ref([])

// Индексы и объекты для редактирования/удаления
const editedIndex = ref(-1)
const defaultItem = {
  id: null,
  topicTitle: '',
  topicDescription: ''
}
const editedItem = ref({ ...defaultItem })

// Заголовок модального окна (динамический)
const formTitle = computed(() => {
  return editedIndex.value === -1 ? 'Новая тема' : 'Редактировать тему'
})

// Конфигурация колонок таблицы
const headers = [
  { title: 'ID', key: 'id', width: '10%', sortable: true },
  { title: 'Название темы', key: 'topicTitle', width: '30%', sortable: true },
  { title: 'Описание контента (для RAG)', key: 'topicDescription', width: '45%', sortable: false },
  { title: 'Действия', key: 'actions', width: '15%', align: 'end', sortable: false }
]

// 📥 Получение всех тем (GET /api/v1/topics)
const fetchTopics = async () => {
  isLoading.value = true
  try {
    const res = await customAxios.get('/v1/topics')
    topics.value = res.data
  } catch (e) {
    console.error('Не удалось загрузить темы:', e)
  } finally {
    isLoading.value = false
  }
}

// ✏️ Открытие окна редактирования
const editItem = (item) => {
  editedIndex.value = topics.value.indexOf(item)
  editedItem.value = { ...item }
  dialog.value = true
}

// 🗑 Открытие окна удаления
const deleteItem = (item) => {
  editedIndex.value = topics.value.indexOf(item)
  editedItem.value = { ...item }
  dialogDelete.value = true
}

// 🚪 Закрытие окна создания/редактирования
const close = () => {
  dialog.value = false
  nextTick(() => {
    editedItem.value = { ...defaultItem }
    editedIndex.value = -1
    topicForm.value?.resetValidation()
  })
}

// 🚪 Закрытие окна подтверждения удаления
const closeDelete = () => {
  dialogDelete.value = false
  nextTick(() => {
    editedItem.value = { ...defaultItem }
    editedIndex.value = -1
  })
}

// 💾 Подтверждение удаления на бэкенде (DELETE /api/v1/topics/{id})
const deleteItemConfirm = async () => {
  isSaving.value = true
  try {
    const targetId = editedItem.value.id
    await customAxios.delete(`/v1/topics/${targetId}`)

    topics.value.splice(editedIndex.value, 1)
    closeDelete()
  } catch (e) {
    console.error('Ошибка при удалении темы:', e)
  } finally {
    isSaving.value = false
  }
}

// 💾 Сохранение изменений или новой записи (POST / PUT)
const save = async () => {
  if (!isFormValid.value) return
  isSaving.value = true
  try {
    if (editedIndex.value > -1) {
      // 🔄 Обновление существующей темы: PUT /api/v1/topics/{id}
       const res = await customAxios.put(`/v1/topics/${editedItem.value.id}`, editedItem.value)
       console.log("res:" + res);
       if(res.status ===202){
        fetchTopics();
        close();
       }
    } else {
      // ➕ Создание новой темы: POST /api/v1/topics
      const res = await customAxios.post('/v1/topics', editedItem.value)
      if(res.status ===201){
       fetchTopics();
       close();
      }
    }
  } catch (e) {
    console.error('Ошибка при сохранении темы:', e)
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  fetchTopics()
})
</script>
<style scoped>
/* Ограничение длины описания темы в таблице, чтобы строки были компактными */
.text-truncate-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
}

/* Вспомогательный отступ между кнопками управления */
.gap-1 {
  gap: 4px;
}
</style>
